import React, { useEffect, useState, useRef } from 'react';
import { Layout1 } from './Layout1';
import MembershipForm from './MembershipForm';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Globe, Zap, Users } from 'lucide-react';
import './landingpage2.css';

function LandingPage1() {
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        if (!video) return;
        if (entry.isIntersecting) {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 }
    );

    if (videoRef.current) observer.observe(videoRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-[#050505] text-white font-['Outfit']">
      {/* Hero Section */}
      <section className="w-full leading-none">
        <img
          src="/images/homepflag.png"
          alt="People of India and Europe for Economic Synergy"
          className="w-full h-auto block"
          fetchpriority="high"
        />
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-widest mb-6">
                Our Mission
              </div>
              <h2 className="text-5xl font-black tracking-tighter mb-8 leading-tight">
                Fostering Strategic <br/> Economic Synergy.
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                The India-Europe Economic Synergy initiative aims to foster sustainable economic
                growth through strategic partnerships, technology exchange, and cultural cooperation.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h4 className="text-3xl font-black text-blue-600">2026</h4>
                  <p className="text-sm text-gray-500 font-bold uppercase tracking-tighter">Strategic Vision</p>
                </div>
                <div>
                  <h4 className="text-3xl font-black text-blue-600">EU-IN</h4>
                  <p className="text-sm text-gray-500 font-bold uppercase tracking-tighter">Economic Corridor</p>
                </div>
              </div>
            </div>

            <div className="flex-1 relative">
              <div className="absolute -inset-4 bg-blue-600/10 rounded-[2rem] blur-2xl" />
              <video
                ref={videoRef}
                src="/videos/indo-euro.mp4"
                loop
                muted
                playsInline
                className="relative w-full h-[400px] object-cover rounded-[2rem] shadow-2xl z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Premium Executive CTA Section */}
      <section className="relative py-32 overflow-hidden bg-black">
        {/* Background Graphic */}
        <div className="absolute inset-0 opacity-40">
           <img src="/images/strengthen.jpg" alt="Partnership" className="w-full h-full object-cover" />
           <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 mb-8">
              <ShieldCheck className="w-4 h-4 text-orange-400" />
              <span className="text-xs font-bold tracking-widest uppercase text-orange-400">Join the Synergy Network</span>
            </div>
            
            <h2 className="text-6xl md:text-8xl font-black tracking-tighter mb-8 leading-[0.9]">
              The Future of Global Trade <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-300">Starts With You.</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl leading-relaxed font-light">
              Uniting policymakers, industry leaders, and innovators to build the most powerful economic corridor between India and Europe.
            </p>

            <div className="text-center">
              <Link 
                to="/join-us"
                className="group relative inline-flex items-center justify-center px-12 py-6 bg-orange-600 text-white rounded-full font-black text-xl hover:bg-orange-500 transition-all duration-300 shadow-[0_20px_50px_rgba(234,88,12,0.3)] hover:shadow-orange-600/50 gap-4"
              >
                Join the Initiative
                <ArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
              
              <div className="flex -space-x-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-12 h-12 rounded-full border-2 border-black bg-gray-800 overflow-hidden">
                    <img src={`https://i.pravatar.cc/100?u=${i}`} alt="Member" />
                  </div>
                ))}
                <div className="w-12 h-12 rounded-full border-2 border-black bg-orange-600 flex items-center justify-center text-[10px] font-bold">
                  +500
                </div>
              </div>
              <span className="text-sm text-gray-500 font-bold uppercase tracking-widest">Active Members</span>
            </div>
          </div>
        </div>
      </section>

      {/* Modal with Membership Form */}
      {showModal && (
        <MembershipForm 
          setShowModal={setShowModal} 
          selectedMembershipType="General Member"
          selectedMembershipFee="TBD"
        />
      )}
    </div>
  );
}

export { LandingPage1 };